import type { TawktoPosition } from '@app/hooks/useTawkto'

const LAUNCHER_SIZE = 56
const GAP = 12
const EDGE_INSET = 12
const PRE_AUTH_ANCHOR = '[data-login-continue-anchor]'

export interface LauncherPlacement {
  bottomPx: number
  centerX: number
}

export interface ChatPanelPlacement {
  bottomPx: number
  leftPx: number
  widthPx: number
  heightPx: number
  /** When set, panel uses top+bottom instead of fixed height (mobile). */
  topPx?: number
}

const APP_MAX_WIDTH = 600
const CHAT_PANEL_SIDE_MARGIN = 16
const CHAT_PANEL_MAX_WIDTH = 420
const CHAT_PANEL_MAX_HEIGHT = 420
const MOBILE_CHAT_BREAKPOINT = 480

const isMobileChatLayout = (): boolean => window.innerWidth <= MOBILE_CHAT_BREAKPOINT

const getHeaderBottomY = (): number => {
  const header = measure('[data-app-header]') ?? measure('.screen-header')
  if (header) return header.bottom
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--standard-header-height').trim()
  const fallback = raw ? parseFloat(raw) : 106
  return Number.isFinite(fallback) ? fallback : 106
}

const computeMobileChatPanelPlacement = (): ChatPanelPlacement => {
  const columnWidth = Math.min(window.innerWidth, APP_MAX_WIDTH)
  const widthPx = Math.max(280, Math.round(columnWidth - CHAT_PANEL_SIDE_MARGIN))
  const leftPx = Math.round((window.innerWidth - columnWidth) / 2 + CHAT_PANEL_SIDE_MARGIN / 2)

  const topPx = Math.round(getHeaderBottomY() + 8)
  const nav = measure('[data-nav-anchor]')
  const bottomY = nav ? nav.top - GAP : window.innerHeight - getInAppLauncherBottom()
  const bottomPx = Math.round(window.innerHeight - bottomY)
  const heightPx = Math.max(240, Math.round(bottomY - topPx))

  return { bottomPx, leftPx, widthPx, heightPx, topPx }
}

const clampCenterX = (centerX: number): number => {
  const half = LAUNCHER_SIZE / 2
  return Math.max(half + 8, Math.min(window.innerWidth - half - 8, centerX))
}

const measure = (selector: string): DOMRect | null => {
  const el = document.querySelector<HTMLElement>(selector)
  if (!el) return null
  const rect = el.getBoundingClientRect()
  return rect.width > 0 && rect.height > 0 ? rect : null
}

/** Fixed launcher `bottom` so the circle sits above a viewport Y coordinate. */
const bottomAboveY = (topY: number, gap = GAP): number =>
  Math.round(window.innerHeight - topY + gap)

/** In-app: above open + menu, else above bottom nav, else fallback. */
const getInAppLauncherBottom = (): number => {
  const creationMenu = measure('[data-creation-modal]')
  if (creationMenu) return bottomAboveY(creationMenu.top)

  const nav = measure('[data-nav-anchor]')
  if (nav) return bottomAboveY(nav.top)

  return 96
}

const getInAppLauncherCenterX = (position: TawktoPosition): number => {
  if (position === 'bottom-right-raw') {
    return clampCenterX(window.innerWidth - EDGE_INSET - LAUNCHER_SIZE / 2)
  }

  const addButton = measure('[data-add-button-wrapper]')
  if (position === 'bottom-right-above-menu' && addButton) {
    return clampCenterX(addButton.left + addButton.width / 2)
  }

  const pill = measure('[data-nav-pill]')
  if (pill) {
    const inset = 28
    if (position === 'bottom-left-above-menu') {
      return clampCenterX(pill.left + inset)
    }
    return clampCenterX(pill.right - inset)
  }

  return clampCenterX(
    position === 'bottom-left-above-menu'
      ? EDGE_INSET + LAUNCHER_SIZE / 2
      : window.innerWidth - EDGE_INSET - LAUNCHER_SIZE / 2,
  )
}

const getPreAuthLauncherPlacement = (): LauncherPlacement | null => {
  const anchor = measure(PRE_AUTH_ANCHOR)
  if (!anchor) return null

  return {
    bottomPx: bottomAboveY(anchor.top),
    centerX: clampCenterX(anchor.right - EDGE_INSET - LAUNCHER_SIZE / 2),
  }
}

export const computeLauncherPlacement = (position: TawktoPosition): LauncherPlacement => {
  const preAuth = getPreAuthLauncherPlacement()
  if (preAuth) return preAuth

  if (position === 'bottom-right-raw') {
    return {
      bottomPx: 20,
      centerX: getInAppLauncherCenterX(position),
    }
  }

  let bottomPx = getInAppLauncherBottom()
  const header = document.querySelector<HTMLElement>('.screen-header')
  if (header) {
    const maxBottom = Math.round(
      window.innerHeight - header.getBoundingClientRect().bottom - LAUNCHER_SIZE - 8,
    )
    bottomPx = Math.min(bottomPx, maxBottom)
  }

  return {
    bottomPx,
    centerX: getInAppLauncherCenterX(position),
  }
}

const getChatPanelWidth = (): number => {
  const containerWidth = Math.min(window.innerWidth, APP_MAX_WIDTH)
  return Math.min(CHAT_PANEL_MAX_WIDTH, Math.round(containerWidth - CHAT_PANEL_SIDE_MARGIN))
}

const clampPanelLeft = (leftPx: number, widthPx: number): number => {
  const minLeft = CHAT_PANEL_SIDE_MARGIN / 2
  const maxLeft = Math.round(window.innerWidth - widthPx - minLeft)
  return Math.max(minLeft, Math.min(maxLeft, leftPx))
}

export const computeChatPanelPlacement = (): ChatPanelPlacement => {
  const preAuth = measure(PRE_AUTH_ANCHOR)
  if (preAuth && isMobileChatLayout()) {
    return computeMobileChatPanelPlacement()
  }

  const widthPx = getChatPanelWidth()
  const heightPx = Math.min(CHAT_PANEL_MAX_HEIGHT, Math.round(window.innerHeight * 0.52))

  if (preAuth) {
    return {
      bottomPx: bottomAboveY(preAuth.top, GAP) + LAUNCHER_SIZE + 8,
      leftPx: clampPanelLeft(Math.round(preAuth.right - widthPx), widthPx),
      widthPx,
      heightPx,
    }
  }

  if (isMobileChatLayout()) {
    return computeMobileChatPanelPlacement()
  }

  const bottomPx = getInAppLauncherBottom()
  const header =
    document.querySelector<HTMLElement>('[data-app-header]') ??
    document.querySelector<HTMLElement>('.screen-header')
  const containerWidth = Math.min(window.innerWidth, APP_MAX_WIDTH)
  const headerRight = header
    ? Math.round(header.getBoundingClientRect().right)
    : Math.round((window.innerWidth + containerWidth) / 2)

  return {
    bottomPx,
    leftPx: clampPanelLeft(headerRight - widthPx, widthPx),
    widthPx,
    heightPx,
  }
}

// ─── Shell-based widget mounting ─────────────────────────────────────────────
//
// Instead of fighting Tawk's JS (which resets #max-widget inline styles on
// its own timer), we reparent #max-widget into a shell <div> that WE control.
// Tawk's JS can only touch #max-widget; it doesn't know about our shell.
// We keep #max-widget filling the shell with a narrow MutationObserver that
// re-applies fill styles the moment Tawk resets them.

export const TAWK_SHELL_ID = 'tipsterizer-tawk-shell'

/** Ref set to true during appendChild so onChatMinimized can ignore the reparent. */
export const mountingRef = { current: false }

let _fillObserver: MutationObserver | null = null
let _applyingFill = false

const applyFillToWidget = (widget: HTMLElement): void => {
  _applyingFill = true
  widget.style.setProperty('position', 'absolute', 'important')
  widget.style.setProperty('inset', '0', 'important')
  widget.style.setProperty('width', '100%', 'important')
  widget.style.setProperty('height', '100%', 'important')
  widget.style.setProperty('max-width', 'none', 'important')
  widget.style.setProperty('max-height', 'none', 'important')
  widget.style.setProperty('transform', 'none', 'important')
  widget.style.setProperty('border-radius', '0', 'important')
  widget.style.setProperty('border', 'none', 'important')
  widget.style.setProperty('margin', '0', 'important')
  widget.style.setProperty('display', 'block', 'important')
  widget.style.setProperty('pointer-events', 'auto', 'important')
  widget.style.setProperty('visibility', 'visible', 'important')
  widget.style.setProperty('opacity', '1', 'important')
  // queueMicrotask clears the flag AFTER all MO microtasks for this batch
  // have fired — so the guard is effective for the entire resulting MO wave.
  queueMicrotask(() => { _applyingFill = false })
}

/**
 * Reparents #max-widget into our shell div (#tipsterizer-tawk-shell) and
 * sets up a narrow MutationObserver to keep it filling the shell.
 * Returns false if either element isn't in the DOM yet.
 */
export const mountTawkWidgetInShell = (): boolean => {
  const shell  = document.getElementById(TAWK_SHELL_ID)
  const widget = document.getElementById('max-widget')
  if (!shell || !widget) {
    console.warn('[tawk-shell] mountTawkWidgetInShell: missing', { shell: !!shell, widget: !!widget })
    return false
  }

  if (widget.parentElement !== shell) {
    mountingRef.current = true
    shell.appendChild(widget)
    // Keep true briefly so onChatMinimized (which fires as a microtask/callback
    // from the DOM change) can detect that this minimize was us, not the user.
    queueMicrotask(() => { mountingRef.current = false })
    console.info('[tawk-shell] reparented #max-widget into shell')
  }

  applyFillToWidget(widget)

  // If widget is a wrapper div, also make its inner iframe fill it.
  if (!(widget instanceof HTMLIFrameElement)) {
    const iframe = widget.querySelector('iframe')
    if (iframe) {
      iframe.style.setProperty('position', 'absolute', 'important')
      iframe.style.setProperty('inset', '0', 'important')
      iframe.style.setProperty('width', '100%', 'important')
      iframe.style.setProperty('height', '100%', 'important')
      iframe.style.setProperty('min-height', '0', 'important')
      iframe.style.setProperty('min-width', '0', 'important')
      iframe.style.setProperty('max-width', 'none', 'important')
      iframe.style.setProperty('max-height', 'none', 'important')
      iframe.style.setProperty('border', 'none', 'important')
      iframe.style.setProperty('pointer-events', 'auto', 'important')
      iframe.style.setProperty('visibility', 'visible', 'important')
      iframe.style.setProperty('opacity', '1', 'important')
    }
  }

  // Watch #max-widget AND its inner iframe for style resets from Tawk's own JS.
  // Scoped to these two elements only — no subtree, no body-wide pollution.
  // The queueMicrotask flag prevents re-entrance from our own setProperty calls.
  _fillObserver?.disconnect()
  _fillObserver = new MutationObserver(() => {
    if (_applyingFill) return
    const w = document.getElementById('max-widget')
    if (!w) return
    console.info('[tawk-shell] Tawk reset detected, re-applying fill')
    applyFillToWidget(w)
    // Also re-apply iframe styles — Tawk may reset the iframe independently.
    if (!(w instanceof HTMLIFrameElement)) {
      const iframe = w.querySelector('iframe')
      if (iframe) {
        iframe.style.setProperty('position', 'absolute', 'important')
        iframe.style.setProperty('inset', '0', 'important')
        iframe.style.setProperty('width', '100%', 'important')
        iframe.style.setProperty('height', '100%', 'important')
        iframe.style.setProperty('min-height', '0', 'important')
        iframe.style.setProperty('min-width', '0', 'important')
        iframe.style.setProperty('max-width', 'none', 'important')
        iframe.style.setProperty('max-height', 'none', 'important')
        iframe.style.setProperty('border', 'none', 'important')
        iframe.style.setProperty('pointer-events', 'auto', 'important')
        iframe.style.setProperty('visibility', 'visible', 'important')
        iframe.style.setProperty('opacity', '1', 'important')
      }
    }
  })
  _fillObserver.observe(widget, { attributes: true, attributeFilter: [ 'style' ] })

  // Also watch the inner iframe if present — Tawk sometimes resets the iframe
  // styles separately from the wrapper div.
  const innerIframe = widget instanceof HTMLIFrameElement ? null : widget.querySelector('iframe')
  if (innerIframe) {
    _fillObserver.observe(innerIframe, { attributes: true, attributeFilter: [ 'style' ] })
  }

  return true
}

/**
 * Moves #max-widget back to document.body and stops the fill observer.
 * Called when the chat is closed.
 */
export const unmountTawkWidgetFromShell = (): void => {
  _fillObserver?.disconnect()
  _fillObserver = null
  _applyingFill = false
  const widget = document.getElementById('max-widget')
  if (widget && widget.parentElement?.id === TAWK_SHELL_ID) {
    widget.style.cssText = ''
    document.body.appendChild(widget)
  }
}

// ─── Hide default Tawk bubble ─────────────────────────────────────────────────

/** Hide tawk's default bubble / teaser; we use our own launcher. */
export const injectHideDefaultTawkBubbleStyles = (): (() => void) => {
  const id = 'tipsterizer-tawk-hide-default'
  if (document.getElementById(id)) return () => undefined

  const style = document.createElement('style')
  style.id = id
  style.textContent = `
    #min-widget,
    #message-preview,
    #chat-bubble,
    #branding-widget {
      visibility: hidden !important;
      pointer-events: none !important;
      opacity: 0 !important;
      max-width: 0 !important;
      max-height: 0 !important;
      overflow: hidden !important;
    }
    body:not([data-tawk-chat-open]) #max-widget,
    body:not([data-tawk-chat-open]) #max-widget iframe,
    body:not([data-tawk-chat-open]) iframe#max-widget {
      visibility: hidden !important;
      pointer-events: none !important;
      opacity: 0 !important;
    }
  `
  document.head.appendChild(style)
  return () => style.remove()
}
